import { ExportOutlined, EyeFilled, PrinterFilled } from '@ant-design/icons';
import { Button, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { Activity, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { netsuite_url } from '../../components/config';
import CustomerSearch from '../../components/ui/CustomerSearch';
import SearchInput from '../../components/ui/SearchInput';
import { moduleName } from '../../constants';
import { getInvoices } from '../../redux/actions/order';
import { getRoutes, ROLES } from '../../routes/Routes';
import { formatCurrency } from '../../utils';

const Invoices = () => {
    const { user } = useSelector((state) => state.authSlice);
    const { module } = useSelector((state) => state.loadingSlice);
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [search, setSearch] = useState('');
    const [customer, setCustomer] = useState(null);

    const fetchInvoices = useDebouncedCallback(async (params = {}) => {
        const res = await getInvoices(params);
        if (res?.ack) {
            setInvoices(res?.invoices || []);
        }
    }, 400);

    const handleSearchChange = (value) => {
        setSearch(value);
        fetchInvoices({ searchText: value, customer_netsuite_id: customer });
    };

    const handleCustomerChange = (value) => {
        setCustomer(value);
        fetchInvoices({ searchText: search, customer_netsuite_id: value });
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'order_date',
            key: 'order_date',
            render: (date) => date && dayjs(date).format('DD-MM-YYYY'),
        },
        {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
            render: (text, record) => (
                <div className="flex align-items-center gap-1">
                    <span className="font-bold">[{record?.customer_code}]</span>
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Internal Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Invoice Code',
            dataIndex: 'invoice_code',
            key: 'invoice_code',
            render: (_, record) => {
                const code = record.invoice_code || record.order_code;
                const internalId = record.inv_internal_id || record.internal_id;

                const link =
                    internalId &&
                    `${netsuite_url}/app/accounting/transactions/salesord.nl?id=${internalId}`;

                return (
                    <div className="flex align-items-center gap-1">
                        {code}
                        <Activity mode={user?.role !== ROLES.CUSTOMER ? 'visible' : 'hidden'}>
                            {link && (
                                <Typography.Link href={link} target="_blank">
                                    <ExportOutlined />
                                </Typography.Link>
                            )}
                        </Activity>
                    </div>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (value) => {
                if (value === 1) return 'Collection';
                if (value === 2) return 'Delivery';
                return '-';
            }
        },
        {
            title: 'Order Value',
            dataIndex: 'order_value',
            key: 'order_value',
            render: (text) => text && formatCurrency(text),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'end',
            render: (text, record) => (
                <Space size="small">
                    <Activity mode={user?.role !== ROLES.CUSTOMER ? 'visible' : 'hidden'}>
                        <Button
                            color="primary"
                            variant="filled"
                            icon={<PrinterFilled className="text-lg" />}
                            href={
                                record?.inv_internal_id &&
                                `${netsuite_url}/app/site/hosting/scriptlet.nl?script=690&deploy=1&compid=5615243&invoiceid=${record?.inv_internal_id}&type=0`
                            }
                        />
                    </Activity>
                    <Button
                        color="primary"
                        variant="filled"
                        onClick={() => navigate(getRoutes().invoice_details.route(record?.id))}
                        icon={<EyeFilled className="text-lg" />}
                        target='_blank'
                    />
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchInvoices();
    }, []);

    return (
        <div className="invoice-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Invoices
                    </Typography.Title>
                </div>

                <div className="flex align-items-center gap-2 flex-wrap">
                    <SearchInput maxWidth="25rem" onSearch={handleSearchChange} />
                    <div className="max-w-25rem w-full">
                        <CustomerSearch
                            size="large"
                            placeholder="Search customer..."
                            showUnassigned={false}
                            onChange={handleCustomerChange}
                            value={customer}
                        />
                    </div>
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id}
                dataSource={invoices}
                pagination={false}
                loading={module === moduleName.GET_INVOICES}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default Invoices;
